import { DesignData, ElementAddress, MediaText, TextStyle, TextWrapping } from 'editor/src/store/design/types';
import { TextAlign } from 'editor/src/store/fonts/types';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';
import type { RootState } from 'editor/src/store/index';

import { extractTextProperties } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

export const getTextStyle = (designData: DesignData | undefined, styleId: string | undefined) =>
  styleId ? designData?.textStyles?.find((style) => style.id === styleId) : undefined;

export interface TextStyleMember {
  element: MediaText;
  address: ElementAddress;
}

/** Every text in the design that follows the style, across all spreads and pages. */
export function getTextStyleMembers(designData: DesignData | undefined, styleId: string): TextStyleMember[] {
  const members: TextStyleMember[] = [];
  designData?.spreads.forEach((spread, spreadIndex) =>
    spread.pages.forEach((page, pageIndex) =>
      page.groups.media?.forEach((element, elementIndex) => {
        if (element.type === 'text' && element.extra.textStyleId === styleId) {
          members.push({ element, address: { spreadIndex, pageIndex, elementIndex } });
        }
      }),
    ),
  );
  return members;
}

export type TextStyleValues = Omit<TextStyle, 'id' | 'name'>;

// How a text without the property renders (textElementDataToFabricProps).
const getTextAlign = (element: MediaText) => element.extra.textAlign ?? TextAlign.left;
const getLetterSpacing = (element: MediaText) => element.extra.letterSpacing ?? 0;

/**
 * Reads the look of a text as a style, e.g. to create a style from the selected text. Alignment,
 * underline, strikethrough and letter spacing are read as the text renders them, so the new
 * style governs them too.
 */
export const getTextStyleValuesFromElement = (element: MediaText): TextStyleValues => ({
  fontFamily: element.extra.fontFamily,
  fontSize: element.extra.fontSize,
  color: element.fill,
  lineHeight: element.extra.lineHeight,
  textAlign: getTextAlign(element),
  underline: !!element.extra.underline,
  linethrough: !!element.extra.linethrough,
  letterSpacing: getLetterSpacing(element),
});

/** The optional properties a style writes onto its texts' `extra` when it sets them. */
const OPTIONAL_EXTRA_KEYS = ['lineHeight', 'textAlign', 'underline', 'linethrough', 'letterSpacing'] as const;

/**
 * The update that makes a text follow the style: the style's values written
 * onto the text, the reference set, and the box re-measured for the new font
 * and size the same way a font edit from the toolbar re-measures it.
 */
export function buildTextStyleElementUpdate(element: MediaText, style: TextStyle): Partial<MediaText> {
  const update: Partial<MediaText> = {
    extra: {
      ...element.extra,
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      ...Object.fromEntries(
        OPTIONAL_EXTRA_KEYS.filter((key) => style[key] !== undefined).map((key) => [key, style[key]]),
      ),
      textStyleId: style.id,
    },
  };
  if (style.color !== undefined) {
    update.fill = style.color;
  }

  // A new font needs a freshly rendered server path, as with the font picker.
  if (style.fontFamily !== element.extra.fontFamily) {
    update.path = undefined;
  }

  Object.assign(update, measureTextStyleElement({ ...element, ...update } as MediaText));
  return update;
}

/**
 * The box of a text re-measured for its font and size, the same way a font edit from the
 * toolbar re-measures it; empty when the text is not re-measured. Same exclusions as
 * applyFontStylesToSelectionOperation: a fitted text sizes itself to its box, and a
 * protected font that is not loaded cannot be measured.
 */
export function measureTextStyleElement(element: MediaText): Partial<MediaText> {
  const { fontFamily, wrapping } = element.extra;
  const isProtectedCustomFont = fontFamily.startsWith('custom-') && !isFontFileLoaded(fontFamily);
  if (isProtectedCustomFont || wrapping === TextWrapping.Fit || wrapping === TextWrapping.FitOneLine) {
    return {};
  }
  const { width, height, sx, sy, x, y } = extractTextProperties(element, undefined);
  return { width, height, sx, sy, x, y };
}

// Sizes read back from fabric carry float noise (28.000000000000004).
const roundSize = (size: number) => Math.round(size * 100) / 100;

/**
 * One spelling per colour, so '#FF0000', '#f00' and 'rgb(255, 0, 0)' compare equal.
 * Alpha is kept; anything unparseable compares as written.
 */
export function normalizeColor(color: string | undefined): string | undefined {
  if (!color) {
    return color;
  }
  const value = color.trim().toLowerCase();
  const shortHex = value.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
  if (shortHex) {
    return `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`;
  }
  const rgb = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (rgb) {
    const hex = `#${rgb
      .slice(1, 4)
      .map((v) => Math.min(255, Number(v)).toString(16).padStart(2, '0'))
      .join('')}`;
    return rgb[4] === undefined || Number(rgb[4]) === 1 ? hex : `${hex}/${Number(rgb[4])}`;
  }
  return value;
}

/**
 * Whether a text that follows the style no longer looks like it: a property the style
 * governs differs from the style's value. Font and size are always the style's; line
 * height, colour, alignment, underline, strikethrough and letter spacing only when the style
 * sets them. Derived, never stored: editing a styled
 * text from the toolbar keeps it in its style (as in Docs/Word), it is just "modified"
 * until the author updates the style from it or resets it to the style.
 */
export function isTextStyleModified(style: TextStyle | undefined, element: MediaText): boolean {
  if (!style) {
    return false;
  }
  return (
    element.extra.fontFamily !== style.fontFamily ||
    roundSize(element.extra.fontSize) !== roundSize(style.fontSize) ||
    (style.lineHeight !== undefined && element.extra.lineHeight !== style.lineHeight) ||
    (style.color !== undefined && normalizeColor(element.fill) !== normalizeColor(style.color)) ||
    (style.textAlign !== undefined && getTextAlign(element) !== style.textAlign) ||
    (style.underline !== undefined && !!element.extra.underline !== style.underline) ||
    (style.linethrough !== undefined && !!element.extra.linethrough !== style.linethrough) ||
    (style.letterSpacing !== undefined && getLetterSpacing(element) !== style.letterSpacing)
  );
}

/**
 * The style update that makes the style match a text: only what the style governs, so a
 * style without e.g. a colour or line height does not start governing one.
 */
export function getTextStyleUpdateFromElement(style: TextStyle, element: MediaText): Partial<TextStyleValues> {
  const values = getTextStyleValuesFromElement(element);
  const governed = (['color', ...OPTIONAL_EXTRA_KEYS] as const).filter(
    (key) => style[key] !== undefined && values[key] !== undefined,
  );
  return {
    fontFamily: values.fontFamily,
    fontSize: values.fontSize,
    ...Object.fromEntries(governed.map((key) => [key, values[key]])),
  };
}

/**
 * A text the current user may not edit directly: personalization-locked by the template, in an
 * editor that honours locks (the buyer's). Style edits skip it, so a style cannot restyle what
 * the template author locked.
 */
export const isTextStyleProtected = (state: RootState, element: MediaText) =>
  !!element.personalizationLocked && !state.hostSettings?.ignorePersonalizationLock;
